import { DataService } from 'api/DataService'
import {
  AntecipationItem,
  ConstructorReceivableParams,
  EligiblePaymentItem,
  ReceivablesCalendar
} from 'models/Receivables'

export type PendingReceivablesStatistics = {
  total_net: number
  total_gross: number
  total_fees: number
  count: number
}

export type PendingReceivablesResponse = {
  records: ConstructorReceivableParams[]
  statistics: PendingReceivablesStatistics
  meta: {
    current_page: number
    next_page: number | null
    prev_page: number | null
    total_pages: number
    total_count: number
  }
}

export type PendingReceivablesFilters = {
  expected_on_from?: string
  expected_on_to?: string
}

const defaultStatistics: PendingReceivablesStatistics = {
  total_net: 0,
  total_gross: 0,
  total_fees: 0,
  count: 0
}

export class ReceivablesService {
  public static async getPendingReceivables(
    page = 1,
    per = 25,
    filters?: PendingReceivablesFilters
  ): Promise<PendingReceivablesResponse> {
    const params = new URLSearchParams({ page: String(page), per: String(per) })
    if (filters?.expected_on_from)
      params.append('expected_on_from', filters.expected_on_from)
    if (filters?.expected_on_to)
      params.append('expected_on_to', filters.expected_on_to)

    const response = await DataService({
      type: 'GET',
      url: `/v1/receivables/pending?${params.toString()}`
    })

    if (response.status === 200) {
      return {
        records: response?.data?.records,
        statistics: response?.data?.statistics ?? defaultStatistics,
        meta: response?.data?.meta
      }
    }

    return {
      records: [],
      statistics: defaultStatistics,
      meta: {
        current_page: 1,
        next_page: null,
        prev_page: null,
        total_pages: 0,
        total_count: 0
      }
    }
  }

  public static async getCalendarDetails(): Promise<
    Array<ReceivablesCalendar>
  > {
    const response = await DataService({
      type: 'GET',
      url: `/v1/receivables/calendar`
    })

    if (response.status === 200) {
      return response?.data?.records
    }

    return [] as Array<ReceivablesCalendar>
  }
  public static async getCalendarByDateDetails(
    date: string
  ): Promise<{ details: Array<ConstructorReceivableParams>; balance: string }> {
    const response = await DataService({
      type: 'GET',
      url: `/v1/receivables/calendar/${date}`
    })

    if (response.status === 200) {
      return {
        details: response?.data?.records,
        balance: response?.data?.amount
      }
    }

    return { details: [], balance: '0.0' }
  }

  public static async getAntecipations(
    page = 1,
    per = 10
  ): Promise<{
    records: AntecipationItem[]
    meta: { total_count: number; total_pages: number; current_page: number }
  }> {
    const response = await DataService({
      type: 'GET',
      url: `/v1/antecipations?page=${page}&per=${per}`
    })

    if (response.status === 200) {
      return {
        records: response?.data?.records ?? [],
        meta: response?.data?.meta ?? {
          total_count: 0,
          total_pages: 1,
          current_page: 1
        }
      }
    }

    return {
      records: [],
      meta: { total_count: 0, total_pages: 1, current_page: 1 }
    }
  }

  public static async getEligiblePayments(): Promise<{
    records: EligiblePaymentItem[]
  }> {
    const response = await DataService({
      type: 'GET',
      url: `/v1/antecipations/eligible_payments`
    })

    if (response.status === 200) {
      return { records: response?.data?.records ?? [] }
    }

    return { records: [] }
  }

  public static async antecipate(
    paymentUuids: string[]
  ): Promise<{ message: string }> {
    const response = await DataService({
      type: 'POST',
      url: `/v2/receivables/antecipate`,
      data: { payment_uuids: paymentUuids }
    })

    if (response.status === 200) {
      return {
        message: response?.data?.message
      }
    }

    return { message: 'Ocorreu um erro.' }
  }
}
